import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPressKitService } from '../services/admin.pressKit.service'
import type { PressKitAssetDocument } from '@/types'
import type { PressKitAssetWithId } from '@/features/press-kit/types'

interface UseAdminPressKitAssetsResult {
  assets: PressKitAssetWithId[]
  loading: boolean
  error: string | null
  createAsset: (data: Omit<PressKitAssetDocument, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>
  updateAsset: (id: string, data: Partial<PressKitAssetDocument>) => Promise<void>
  updateAssetOrder: (id: string, order: number) => Promise<void>
  deleteAsset: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminPressKitAssets(): UseAdminPressKitAssetsResult {
  const [assets, setAssets] = useState<PressKitAssetWithId[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPressKitService
      .getAllAssets()
      .then(setAssets)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load assets'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createAsset = useCallback(
    async (data: Omit<PressKitAssetDocument, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
      await adminPressKitService.createAsset(data, assets.length)
      refresh()
    },
    [assets.length, refresh]
  )

  const updateAsset = useCallback(async (id: string, data: Partial<PressKitAssetDocument>): Promise<void> => {
    await adminPressKitService.updateAsset(id, data)
    refresh()
  }, [refresh])

  const updateAssetOrder = useCallback(async (id: string, order: number): Promise<void> => {
    await adminPressKitService.updateAssetOrder(id, order)
    refresh()
  }, [refresh])

  const deleteAsset = useCallback(async (id: string): Promise<void> => {
    await adminPressKitService.deleteAsset(id)
    refresh()
  }, [refresh])

  return { assets, loading, error, createAsset, updateAsset, updateAssetOrder, deleteAsset, refresh }
}
